import React from 'react';
import { cn } from '@/utils/cn';

export interface SwitchProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'role' | 'onChange'> {
  checked: boolean;
  /** Called with the new value when the user flips the switch. */
  onCheckedChange?: (next: boolean) => void;
}

/**
 * On/off setting. Violet when on (a selection state), neutral steel when off. The track is 44px wide and the
 * hit area is padded out to 44px tall, so it works as a touch target. Name it with `aria-label` or `aria-labelledby`.
 */
const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ checked, onCheckedChange, className, onClick, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={(e) => {
        onClick?.(e);
        if (!e.defaultPrevented) onCheckedChange?.(!checked);
      }}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border border-transparent transition-colors',
        'before:absolute before:-inset-2 before:content-[""]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui-card',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-accent-fill' : 'bg-ui-border3',
        className,
      )}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none inline-block h-[18px] w-[18px] rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-[22px]' : 'translate-x-[2px]',
        )}
      />
    </button>
  ),
);

Switch.displayName = 'Switch';

export { Switch };
